import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import styled from 'styled-components';

import { changeFormField, setNewForm } from 'apps/auth/actions/formAction';
import { Text } from 'apps/auth/components/common/Text/Text';
import { getField, validationAndUpdateValue } from 'apps/auth/components/form-multi/utils';
import { useForm } from 'apps/auth/hooks/selectors';
import type { AppDispatch } from 'apps/auth/redux/store';
import media from 'apps/auth/theme/media';

import PasscodeDot from './PasscodeDot';

const DotsContainer = styled.div`
    display: flex;
    justify-content: center;
    column-gap: 24px;
`;

const PassCodeWrapper = styled.div`
    height: 0;
    overflow: hidden;
    position: absolute;

    input {
        font-size: 20px;
        color: inherit;
    }
`;

const PassCodeOverlay = styled.div`
    position: absolute;
    width: 100%;
    height: 100%;
    left: 0;
    right: 0;
`;

const HintContainer = styled.div`
    margin-top: 24px;
    padding: 0 16px;
    text-align: center;

    ${media.desktop1920} {
        padding: 0 24px;
    }
`;

type PasscodeField = {
    value?: string;
    [key: string]: unknown;
};

type PasscodeFieldsMap = Record<string, PasscodeField>;

type PasscodeForm = {
    fields?: {
        passcode?: PasscodeField;
        [key: string]: PasscodeField | undefined;
    };
};

interface InputPasscodeProps {
    formName: string;
    maxLength: number;
    afterPasscodeEnterHandler: (field: PasscodeField) => void;
    errorMessage?: string;
    removeMessageError?: () => void;
    handleKeyboardToggle?: (isOpened: boolean) => void;
}

export const InputPasscode: React.FC<InputPasscodeProps> = (props) => {
    const dispatch = useDispatch<AppDispatch>();
    const form = useForm<PasscodeForm>(props.formName);
    const passcodeRef = useRef<HTMLInputElement | null>(null);
    const timerRef = useRef<number | null>(null);
    const keyboardToggleRef = useRef<InputPasscodeProps['handleKeyboardToggle']>(props.handleKeyboardToggle);

    useEffect(() => {
        keyboardToggleRef.current = props.handleKeyboardToggle;
    }, [props.handleKeyboardToggle]);

    useEffect(() => {
        const newFields: PasscodeFieldsMap = {
            passcode: getField('passcode', ''),
        };
        void dispatch(setNewForm(props.formName, newFields));

        const handleViewportResize = () => {
            if (typeof window === 'undefined') {
                return;
            }
            const { visualViewport } = window;
            if (!visualViewport) {
                return;
            }
            const initialScreenSize = window.innerHeight;
            const viewportHeight = visualViewport.height;
            keyboardToggleRef.current?.(viewportHeight < initialScreenSize);
        };

        window.visualViewport?.addEventListener('resize', handleViewportResize);

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            window.visualViewport?.removeEventListener('resize', handleViewportResize);
        };
    }, [dispatch, props.formName]);

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            passcodeRef.current?.focus();
        }, 100);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [props.errorMessage]);

    const passcodeField = form?.fields?.passcode;
    const passcodeValue = passcodeField?.value ?? '';
    const passcodeLength = passcodeValue.length;

    const changeFormFieldHandler = (formName: string, field: PasscodeField) => {
        return dispatch(changeFormField(formName, field));
    };

    const handlerBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        event.preventDefault();
        if (passcodeLength < props.maxLength) {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            timerRef.current = window.setTimeout(() => {
                passcodeRef.current?.focus();
            }, 100);
        }
    };

    const changeValue = (currentValue: string) => {
        const normalizedValue = currentValue ? currentValue.replace(/\D/g, '') : '';
        const currentField = form?.fields?.passcode;
        if (!currentField) {
            return;
        }

        void validationAndUpdateValue(props.formName, normalizedValue, currentField, changeFormFieldHandler).then((newField: PasscodeField) => {
            if (normalizedValue.length === props.maxLength) {
                props.afterPasscodeEnterHandler(newField);
            } else if (props.errorMessage && normalizedValue.length !== 0) {
                props.removeMessageError?.();
            }
        });
    };

    const handlerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        changeValue(event.target.value);
    };

    const handleClickOverlay = () => {
        passcodeRef.current?.focus();
    };

    return (
        <>
            <div>
                <DotsContainer>
                    {Array.from({ length: props.maxLength }, (_, index) => (
                        <PasscodeDot key={`dot_key_${index}`} currentLength={passcodeLength} dotIndex={index + 1} />
                    ))}
                </DotsContainer>
                {props.errorMessage && (
                    <HintContainer>
                        <Text size="secondary" color="error">
                            {props.errorMessage}
                        </Text>
                    </HintContainer>
                )}
            </div>
            <PassCodeWrapper>
                <input
                    ref={passcodeRef}
                    value={passcodeValue}
                    autoComplete="off"
                    onBlur={handlerBlur}
                    onChange={handlerChange}
                    type="tel"
                    inputMode="numeric"
                    maxLength={props.maxLength}
                />
            </PassCodeWrapper>
            <PassCodeOverlay onClick={handleClickOverlay} />
        </>
    );
};

export default InputPasscode;
